import type { NodeTypeDef } from '../core/types';

/**
 * Height-map derivations: the nodes that turn a grayscale elevation field into
 * the ancillary maps a PBR shader needs. All of them read height and nothing
 * else, which is why a well-built graph converges on one height chain.
 */
export const meshNodes: NodeTypeDef[] = [
  {
    id: 'mesh.normal',
    label: 'Normal',
    category: 'mesh',
    description: 'Tangent-space normal map from a height map.',
    guidance:
      'Where surface detail becomes lighting. Sobel sampling makes it robust to noise. Choose DirectX if your target engine expects green-down (Unreal, most Substance exports); OpenGL for green-up (Unity, glTF).',
    keywords: ['bump', 'height to normal', 'tangent', 'surface', 'detail'],
    inputs: [{ name: 'height', label: 'Height', kind: 'grayscale' }],
    output: 'color',
    params: [
      {
        name: 'intensity',
        label: 'Intensity',
        type: 'float',
        default: 8,
        min: 0,
        max: 64,
        step: 0.01,
        help: 'Steepness of the derived slope. High values exaggerate fine detail.',
      },
      {
        name: 'radius',
        label: 'Sample Radius',
        type: 'float',
        default: 1,
        min: 0.25,
        max: 8,
        step: 0.01,
        help: 'Widen to skip over noise and capture the larger form. Measured in pixels at 512px and held to the same material size at other resolutions.',
      },
      {
        name: 'space',
        label: 'Space',
        type: 'enum',
        default: 'opengl',
        options: [
          { value: 'opengl', label: 'OpenGL (+Y up)' },
          { value: 'directx', label: 'DirectX (+Y down)' },
        ],
      },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        vec2 t = pixelUnit() * p_radius;
        float tl = in0gl(uv + vec2(-t.x,  t.y));
        float tm = in0gl(uv + vec2( 0.0,  t.y));
        float tr = in0gl(uv + vec2( t.x,  t.y));
        float ml = in0gl(uv + vec2(-t.x,  0.0));
        float mr = in0gl(uv + vec2( t.x,  0.0));
        float bl = in0gl(uv + vec2(-t.x, -t.y));
        float bm = in0gl(uv + vec2( 0.0, -t.y));
        float br = in0gl(uv + vec2( t.x, -t.y));

        // Sobel rather than central differences: the 3×3 support averages out
        // single-texel noise that would otherwise show up as normal-map speckle.
        float dx = (tr + 2.0 * mr + br) - (tl + 2.0 * ml + bl);
        float dy = (tl + 2.0 * tm + tr) - (bl + 2.0 * bm + br);

        vec3 n = normalize(vec3(-dx * p_intensity, -dy * p_intensity, 1.0));
        if (p_space == K_space_directx) n.y = -n.y;
        return vec4(n * 0.5 + 0.5, 1.0);
      }
    `,
  },

  {
    id: 'mesh.ao',
    label: 'Ambient Occlusion',
    category: 'mesh',
    description: 'Screen-space ambient occlusion from a height map.',
    guidance:
      'Darkens crevices. Multiply the result into base colour for contact shading that no amount of roughness tweaking can fake. Raise Radius for broad cavity shadowing, lower it for tight creases.',
    keywords: ['occlusion', 'cavity', 'shadow', 'crevice', 'contact', 'dirt'],
    inputs: [{ name: 'height', label: 'Height', kind: 'grayscale' }],
    output: 'grayscale',
    params: [
      {
        name: 'radius',
        label: 'Radius',
        type: 'float',
        default: 16,
        min: 1,
        max: 128,
        step: 0.1,
        help: 'Occlusion radius in pixels at 512px, held to the same material size at other resolutions.',
      },
      {
        name: 'intensity',
        label: 'Intensity',
        type: 'float',
        default: 1,
        min: 0,
        max: 4,
        step: 0.01,
      },
      {
        name: 'height',
        label: 'Height Scale',
        type: 'float',
        default: 1,
        min: 0.01,
        max: 8,
        step: 0.01,
        help: 'How tall the height map is taken to be, relative to the radius.',
      },
      {
        name: 'samples',
        label: 'Samples',
        type: 'int',
        default: 16,
        min: 4,
        max: 48,
        group: 'Quality',
      },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        float h0 = in0gl(uv);
        int n = p_samples;
        float occ = 0.0;
        // Golden-angle spiral: even coverage without the axis-aligned artefacts
        // a square kernel produces on directional patterns.
        const float GOLDEN = 2.39996323;
        for (int i = 0; i < 48; i++) {
          if (i >= n) break;
          float fi = float(i) + 0.5;
          float a = fi * GOLDEN;
          float r = sqrt(fi / float(n)) * p_radius;
          vec2 o = vec2(cos(a), sin(a)) * r * pixelUnit();
          float hs = in0gl(uv + o);
          // Anything higher than us within the radius occludes, falling off with distance.
          float diff = (hs - h0) * p_height;
          float falloff = 1.0 - saturate(r / max(p_radius, 1e-4));
          occ += max(diff, 0.0) * falloff;
        }
        occ = occ / float(n) * p_intensity * 8.0;
        return vec4(vec3(saturate(1.0 - occ)), 1.0);
      }
    `,
  },

  {
    id: 'mesh.curvature',
    label: 'Curvature',
    category: 'mesh',
    description: 'Convex and concave edges from a height map.',
    guidance:
      'Peaks come out bright, valleys dark, flats mid-grey. The canonical source for edge wear: run it through Histogram Scan to isolate convex edges, then use that as a mask for scratched or exposed metal.',
    keywords: ['edge wear', 'convex', 'concave', 'cavity', 'peaks', 'scratch mask'],
    inputs: [{ name: 'height', label: 'Height', kind: 'grayscale' }],
    output: 'grayscale',
    params: [
      {
        name: 'intensity',
        label: 'Intensity',
        type: 'float',
        default: 8,
        min: 0,
        max: 128,
        step: 0.01,
      },
      {
        name: 'radius',
        label: 'Radius',
        type: 'float',
        default: 1,
        min: 0.25,
        max: 16,
        step: 0.01,
        help: 'Sample radius in pixels at 512px, held to the same material size at other resolutions.',
      },
      {
        name: 'mode',
        label: 'Mode',
        type: 'enum',
        default: 'both',
        options: [
          { value: 'both', label: 'Convex + Concave' },
          { value: 'convex', label: 'Convex only' },
          { value: 'concave', label: 'Concave only' },
        ],
      },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        vec2 t = pixelUnit() * p_radius;
        float c = in0gl(uv);
        // Discrete Laplacian: the second derivative of height is curvature.
        float lap =
          in0gl(uv + vec2( t.x, 0.0)) +
          in0gl(uv + vec2(-t.x, 0.0)) +
          in0gl(uv + vec2(0.0,  t.y)) +
          in0gl(uv + vec2(0.0, -t.y)) -
          4.0 * c;
        float v = lap * p_intensity;
        if (p_mode == K_mode_convex) return vec4(vec3(saturate(-v)), 1.0);
        if (p_mode == K_mode_concave) return vec4(vec3(saturate(v)), 1.0);
        return vec4(vec3(saturate(0.5 - v * 0.5)), 1.0);
      }
    `,
  },

  {
    id: 'mesh.normalBlend',
    label: 'Normal Blend',
    category: 'mesh',
    description: 'Combine two normal maps correctly.',
    guidance:
      'Use this instead of Blend whenever both inputs are normal maps. A plain blend averages the encoded vectors, which does not produce a unit normal and flattens detail toward blue — combining a large-form normal with a fine-detail one through Blend visibly loses the detail. Reoriented mode is the most accurate; Whiteout is cheaper and usually indistinguishable.',
    keywords: ['normal', 'combine', 'detail', 'reoriented', 'whiteout', 'rnm', 'overlay'],
    inputs: [
      { name: 'base', label: 'Base', kind: 'color' },
      { name: 'detail', label: 'Detail', kind: 'color' },
      { name: 'mask', label: 'Mask', kind: 'grayscale', optional: true, fallback: [1, 1, 1, 1] },
    ],
    output: 'color',
    params: [
      {
        name: 'mode',
        label: 'Mode',
        type: 'enum',
        default: 'reoriented',
        options: [
          { value: 'reoriented', label: 'Reoriented (accurate)' },
          { value: 'whiteout', label: 'Whiteout (cheap)' },
        ],
      },
      {
        name: 'intensity',
        label: 'Detail Intensity',
        type: 'float',
        default: 1,
        min: 0,
        max: 4,
        step: 0.01,
        help: 'Scales the detail normal before combining.',
      },
      {
        name: 'space',
        label: 'Space',
        type: 'enum',
        default: 'opengl',
        options: [
          { value: 'opengl', label: 'OpenGL (+Y up)' },
          { value: 'directx', label: 'DirectX (+Y down)' },
        ],
        group: 'Output',
      },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        vec3 base = normalize(in0(uv).rgb * 2.0 - 1.0);
        vec3 detail = in1(uv).rgb * 2.0 - 1.0;
        detail.xy *= p_intensity;
        detail = normalize(detail);

        vec3 result;
        if (p_mode == K_mode_whiteout) {
          // Sum the tangent-space slopes and keep the base z. Cheap, and stable
          // because it never divides by a value that can approach zero.
          result = normalize(vec3(base.xy + detail.xy, base.z * detail.z));
        } else {
          // Reoriented normal mapping: build the rotation that takes +Z onto the
          // base normal, then apply it to the detail normal. This is the one
          // that stays correct when the base is steep.
          vec3 tv = base + vec3(0.0, 0.0, 1.0);
          vec3 uvv = detail * vec3(-1.0, -1.0, 1.0);
          result = normalize(tv * dot(tv, uvv) / max(tv.z, 1e-4) - uvv);
        }

        result = normalize(mix(base, result, in2g(uv)));
        if (p_space == K_space_directx) result.y = -result.y;
        return vec4(result * 0.5 + 0.5, 1.0);
      }
    `,
  },

  {
    id: 'mesh.heightBlend',
    label: 'Height Blend',
    category: 'mesh',
    description: 'Blend two layers by which one is physically taller.',
    guidance:
      'Physically-plausible layering. Where a plain Blend fades one image into another, this lets the taller surface win — so gravel sits between bricks instead of ghosting over them. Contrast sharpens the transition.',
    keywords: ['height lerp', 'displacement blend', 'layer', 'material blend', 'gravel'],
    inputs: [
      { name: 'aColor', label: 'A Color', kind: 'color' },
      { name: 'aHeight', label: 'A Height', kind: 'grayscale' },
      { name: 'bColor', label: 'B Color', kind: 'color' },
      { name: 'bHeight', label: 'B Height', kind: 'grayscale' },
    ],
    output: 'color',
    params: [
      {
        name: 'bias',
        label: 'Bias',
        type: 'float',
        default: 0,
        min: -1,
        max: 1,
        step: 0.001,
        help: 'Positive lifts B, letting it cover more of A.',
      },
      {
        name: 'contrast',
        label: 'Contrast',
        type: 'float',
        default: 0.85,
        min: 0,
        max: 1,
        step: 0.001,
        help: '1 is a hard height cut; lower values cross-fade the boundary.',
      },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        float ha = in1g(uv);
        float hb = in3g(uv) + p_bias;
        float width = max(1.0 - p_contrast, 1e-4) * 0.5;
        float t = smoothstep(-width, width, hb - ha);
        return mix(in0(uv), in2(uv), t);
      }
    `,
  },
];
